import { useEffect, useMemo, useRef } from 'react';
import type { ConsoleEventRecord } from '@/db/codexDB';
import { ProposalEventCard } from '@/features/cards/ProposalBundleCard';
import { Markdown } from './Markdown';

/** 도구명 → 작가 언어(노이즈 도구는 null=숨김). 엔진 어휘는 1급 노출 금지(§3.4). */
const TOOL_LABEL: Record<string, string | null> = {
  ToolSearch: null,
  mcp__loom__read_canon: '세계 읽기',
  mcp__loom__read_manuscript: '원고 읽기',
  mcp__loom__run_consistency: '정합성 검사',
  mcp__loom__compute_ripple: '파급 계산',
  mcp__loom__propose_change: '변경 제안 작성',
  mcp__loom__build_scene_context: '집필 컨텍스트(봉인)',
  mcp__loom__propose_scene: '씬 제안 작성',
  mcp__loom__os_impact: '캐릭터 OS 파급',
};

function toolLabel(tool?: string): string | null {
  if (!tool) return '도구';
  if (tool in TOOL_LABEL) return TOOL_LABEL[tool];
  if (tool.startsWith('mcp__loom__')) return tool.slice('mcp__loom__'.length);
  return null; // 내부/플랫폼 도구는 숨김
}

interface TimelineItem {
  ev: ConsoleEventRecord;
  label?: string;
}

/** 렌더 전 정제 — model= 노이즈·내부 도구 숨김, 결과를 도구 라벨에 결속, assistant=result 중복 제거(레거시 포함). */
function refine(events: ConsoleEventRecord[]): TimelineItem[] {
  const out: TimelineItem[] = [];
  let lastTool: string | null = null;
  let lastAssistant = '';
  for (const ev of events) {
    if (ev.type === 'system' && (ev.text ?? '').startsWith('model=')) continue;
    if (ev.type === 'tool_use') {
      const label = toolLabel(ev.tool);
      if (!label) continue;
      lastTool = label;
      out.push({ ev, label });
      continue;
    }
    if (ev.type === 'tool_result') {
      const label = lastTool;
      lastTool = null;
      if (!label) continue; // 숨긴 도구의 결과도 숨김
      if (!(ev.text ?? '').trim()) continue;
      out.push({ ev, label });
      continue;
    }
    if (ev.type === 'assistant') {
      lastAssistant = (ev.text ?? '').trim();
      out.push({ ev });
      continue;
    }
    if (ev.type === 'result') {
      if (ev.ok !== false && (ev.text ?? '').trim() === lastAssistant) continue; // 최종문 중복 숨김
      if (ev.ok !== false && !(ev.text ?? '').trim()) continue;
      out.push({ ev });
      continue;
    }
    out.push({ ev });
  }
  return out;
}

const ARTIFACT_TYPES = new Set<ConsoleEventRecord['type']>(['assistant', 'result', 'tool_result', 'proposal']);

export function ConsoleTimeline({
  events,
  className,
  onOpenArtifact,
}: {
  events: ConsoleEventRecord[];
  className?: string;
  /** F0: 카드 클릭 → 우측 artifact 패널로 상세(홈에서만 전달). */
  onOpenArtifact?: (ev: ConsoleEventRecord) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const items = useMemo(() => refine(events), [events]);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight });
  }, [items.length]);

  return (
    <div ref={ref} className={className}>
      {items.length === 0 ? (
        <p className="p-2 text-[11px] leading-5 text-faint">
          지시를 입력하면 에이전트가 Loom 도메인(canon·원고·엔진) 위에서 실행한다 — 전부 proposed.
        </p>
      ) : (
        <div className="space-y-2 p-0.5">
          {items.map(({ ev, label }) =>
            onOpenArtifact && ARTIFACT_TYPES.has(ev.type) ? (
              <div
                key={ev.id}
                onClick={() => onOpenArtifact(ev)}
                title="클릭 — 우측 패널에 상세"
                className="cursor-pointer rounded-md transition hover:bg-white/[0.03]"
              >
                <EventLine ev={ev} label={label} />
              </div>
            ) : (
              <EventLine key={ev.id} ev={ev} label={label} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function EventLine({ ev, label }: { ev: ConsoleEventRecord; label?: string }) {
  switch (ev.type) {
    case 'proposal':
      return ev.proposalId ? <ProposalEventCard proposalId={ev.proposalId} /> : null;
    case 'prompt':
      return (
        <div className="rounded-md border border-star/25 bg-star/[0.06] px-3 py-2">
          {ev.contextNote && <span className="mb-0.5 block font-mono text-[10px] text-star/60">⌖ {ev.contextNote} 컨텍스트 주입</span>}
          <p className="whitespace-pre-wrap text-[14px] leading-7 text-ink">{ev.text}</p>
        </div>
      );
    case 'assistant':
      return <Markdown text={ev.text ?? ''} className="px-1" />;
    case 'tool_use':
      return (
        <p className="flex items-center gap-1.5 px-1 font-mono text-[11px] text-star/70">
          <span className="inline-block size-1 animate-pulse rounded-full bg-star/70" />
          {label}…
        </p>
      );
    case 'tool_result':
      return (
        <details className="rounded-md border border-line bg-bg-2/50 px-2.5 py-1.5">
          <summary className="cursor-pointer font-mono text-[11px] text-fainter transition hover:text-dim">
            ✓ {label} — 결과 펼치기
          </summary>
          <pre className="mt-1.5 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-[12px] leading-6 text-dim">{ev.text}</pre>
        </details>
      );
    case 'result':
      return ev.ok === false ? (
        <div className="rounded-md border border-red-400/30 bg-red-950/20 px-3 py-2">
          <Markdown text={ev.text ?? '실패'} />
        </div>
      ) : (
        <Markdown text={ev.text ?? ''} className="px-1" />
      );
    case 'error':
      return <p className="px-1 text-[12.5px] text-red-300">에러: {ev.text}</p>;
    default:
      return <p className="px-1 font-mono text-[11px] text-fainter">· {ev.text}</p>;
  }
}
